import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { RestApiService } from '../api/rest-api.service';
import { retryTransientRead } from '../api/retry-transient-read';

import {
  ORGANISATION_DELETE_ORGANISATION_ENDPOINT,
  ORGANISATION_GET_ADMIN_ORGANISATIONS_ENDPOINT,
  ORGANISATION_GET_INVOLVED_ORGANISATIONS_ENDPOINT,
  ORGANISATION_GET_ORGANISATIONS_ENDPOINT,
  ORGANISATION_GET_ORGANISATIONS_SEARCH_SCHEMA_ENDPOINT,
  ORGANISATION_GET_ORGANISATION_ENDPOINT,
  ORGANISATION_POST_ORGANISATION_ENDPOINT,
  ORGANISATION_POST_RESTORE_ORGANISATION_ENDPOINT,
  ORGANISATION_PUT_ORGANISATION_ENDPOINT,
} from '../api/api-endpoints';
import { formatString } from '../utils/string.utils';
import { buildSearchQueryString } from '../utils/search-query-params.utils';
import {
  CreateOrganisationInput,
  OrganisationContract,
  OrganisationListResponse,
  OrganisationSearchSchema,
  UpdateOrganisationInput,
} from '../../apps/configuration/org.types';
import { SearchListRequestPayload } from '../../shared/search-filter-pagination/search-bar/search-bar.types';

export interface OrganisationState {
  id?: string;
  name?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class OrganisationService {
  private organisationSubject = new BehaviorSubject<OrganisationState | null>(null);
  organisationData$ = this.organisationSubject.asObservable();
  organisation: OrganisationState | null = null;

  constructor(private restApi: RestApiService) {}

  get state(): OrganisationState | null {
    return this.organisationSubject.value;
  }

  setOrganisationId(organisationId: string) {
    this.organisation = { id: organisationId };
    queueMicrotask(() => {
      this.organisationSubject.next(this.organisation);
    });
    void this.fetchOrganisationById(organisationId);
  }

  async fetchOrganisationById(organisationId: string): Promise<OrganisationContract | null> {
    try {
      const organisation = await this.getOrganisationById(organisationId);
      this.organisation = { ...organisation, name: organisation.name || undefined };
      queueMicrotask(() => {
        this.organisationSubject.next(this.organisation);
      });
      return organisation;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  createOrganisation(createOrganisationInput: CreateOrganisationInput) {
    // Empty optional form inputs mean absence, not a provided invalid UUID,
    // email, URL, legal name, or ISO3 country in the owner DTO.
    return this.restApi.post<OrganisationContract>(ORGANISATION_POST_ORGANISATION_ENDPOINT, {
      ...createOrganisationInput,
      parent_organisation_id: createOrganisationInput.parent_organisation_id?.trim() || null,
      legal_name: createOrganisationInput.legal_name || null,
      website: createOrganisationInput.website?.trim() || null,
      email: createOrganisationInput.email?.trim() || null,
      fiscal_country: createOrganisationInput.fiscal_country?.trim() || null,
    });
  }

  async listOrganisations(payload?: Partial<SearchListRequestPayload>): Promise<OrganisationListResponse> {
    const queryString = buildSearchQueryString(payload);
    return firstValueFrom(
      this.restApi.get<OrganisationListResponse>(`${ORGANISATION_GET_ORGANISATIONS_ENDPOINT}${queryString}`)
    );
  }

  async getOrganisationSearchSchema(): Promise<OrganisationSearchSchema> {
    return firstValueFrom(
      this.restApi.get<OrganisationSearchSchema>(ORGANISATION_GET_ORGANISATIONS_SEARCH_SCHEMA_ENDPOINT)
    );
  }

  async getOrganisationById(organisationId: string): Promise<OrganisationContract> {
    const endpoint = formatString(ORGANISATION_GET_ORGANISATION_ENDPOINT, organisationId);
    return firstValueFrom(this.restApi.get<OrganisationContract>(endpoint));
  }

  updateOrganisation(organisationId: string, payload: UpdateOrganisationInput) {
    return this.restApi.put<OrganisationContract>(
      formatString(ORGANISATION_PUT_ORGANISATION_ENDPOINT, organisationId),
      {
        ...payload,
        ...(payload.parent_organisation_id !== undefined
          ? { parent_organisation_id: payload.parent_organisation_id?.trim() || null } : {}),
        ...(payload.legal_name !== undefined ? { legal_name: payload.legal_name || null } : {}),
        ...(payload.website !== undefined ? { website: payload.website?.trim() || null } : {}),
        ...(payload.email !== undefined ? { email: payload.email?.trim() || null } : {}),
        ...(payload.fiscal_country !== undefined
          ? { fiscal_country: payload.fiscal_country?.trim() || null } : {}),
      }
    );
  }

  archiveOrganisation(organisationId: string) {
    return this.restApi.delete<OrganisationContract>(formatString(ORGANISATION_DELETE_ORGANISATION_ENDPOINT, organisationId));
  }

  restoreOrganisation(organisationId: string) {
    return this.restApi.post<OrganisationContract>(
      formatString(ORGANISATION_POST_RESTORE_ORGANISATION_ENDPOINT, organisationId),
      {}
    );
  }

  getOrganisation() {
    return this.organisation;
  }

  getOrganisationsIAdmin() {
    return firstValueFrom(this.restApi.get<OrganisationContract[]>(ORGANISATION_GET_ADMIN_ORGANISATIONS_ENDPOINT).pipe(retryTransientRead()));
  }

  getOrganisationsImInvolved() {
    return firstValueFrom(this.restApi.get<OrganisationContract[]>(ORGANISATION_GET_INVOLVED_ORGANISATIONS_ENDPOINT));
  }

  removeSelectedOrganisation() {
    this.organisation = null;
    this.organisationSubject.next(null);
  }
}
